import React, { useEffect, useState } from "react";
import { Star, BadgeCheck, MessageSquareReply } from "lucide-react";
import { api } from "@/lib/api";

const StarRow = ({ value, size = "w-4 h-4" }) => (
  <div className="flex gap-0.5 text-gold">
    {[...Array(5)].map((_, i) => (
      <Star key={i} className={`${size} ${i < value ? "fill-gold" : "fill-none text-navy/20"}`} />
    ))}
  </div>
);

export const ProductReviews = ({ productId }) => {
  const [reviews, setReviews] = useState([]);
  const [summary, setSummary] = useState({ count: 0, average: 0 });

  useEffect(() => {
    if (!productId) return;
    const params = { product_id: productId };
    api.get("/reviews", { params }).then((r) => setReviews(r.data)).catch(() => {});
    api.get("/reviews/summary", { params }).then((r) => setSummary(r.data)).catch(() => {});
  }, [productId]);

  if (reviews.length === 0) return null;

  return (
    <section data-testid="product-reviews" className="mt-16 border-t border-navy/15 pt-10">
      <div className="flex items-center gap-3 flex-wrap">
        <h2 className="font-display text-3xl text-navy">Customer Reviews</h2>
        <span className="flex items-center gap-2">
          <StarRow value={Math.round(summary.average)} size="w-5 h-5" />
          <span className="text-ink/70 text-sm font-semibold" data-testid="product-review-average">
            {summary.average.toFixed(1)} · {summary.count} review{summary.count === 1 ? "" : "s"}
          </span>
        </span>
      </div>

      <div className="mt-6 grid gap-5 sm:grid-cols-2">
        {reviews.map((rev) => (
          <article key={rev.id} data-testid={`product-review-${rev.id}`} className="bg-white rounded-xl border border-navy/15 p-5">
            <div className="flex items-center justify-between gap-2">
              <StarRow value={rev.rating} />
              {rev.verified && (
                <span className="inline-flex items-center gap-1 text-[11px] font-bold rounded-full px-2.5 py-1 bg-green-100 text-green-700">
                  <BadgeCheck className="w-3.5 h-3.5" /> Verified Buyer
                </span>
              )}
            </div>
            {rev.title && <h3 className="mt-2 font-bold text-navy text-sm">{rev.title}</h3>}
            <p className="mt-1 text-ink/80 text-sm leading-relaxed">"{rev.text}"</p>
            {rev.owner_response && (
              <div className="mt-3 rounded-lg bg-cream border border-gold/40 p-3">
                <p className="flex items-center gap-1.5 text-xs font-bold text-navy uppercase">
                  <MessageSquareReply className="w-3.5 h-3.5 text-brick" /> JohnnyMart replied
                </p>
                <p className="mt-1 text-sm text-ink/80">{rev.owner_response}</p>
              </div>
            )}
            <div className="mt-3 text-xs text-ink/50 font-bold text-navy">{rev.author}</div>
          </article>
        ))}
      </div>
    </section>
  );
};
